import { useCallback, useState } from 'react';

import { Icon, IconAttention, IconInfo } from '@flixbus/honeycomb-icons-react';
import { Input, List, Select, SelectOption } from '@flixbus/honeycomb-react';

import { minutesToDuration } from 'utils/datetime';

import {
	CANNOT_CANCEL_OVERLAPPING_BANNER_TEXT,
	DELAY_LABEL,
	MINIMUM_RECOMMENDED_DELAY_TEXT,
	OVERLAPPING_ACTION_LABEL,
	OVERLAPPING_ACTION_OPTION_LABELS,
	OVERLAPPING_ACTION_OPTIONS,
	OVERLAPPING_BANNER_TEXT,
} from './text';

import styles from './styles.module.css';

const MINUTES_PER_HOUR = 60;
const DURATION_PATTERN = /^(\d{0,3}(:\d{0,2})?)?$/;

const parseDuration = (value) => {
	const match = value.match(/^(\d{1,3}):(\d{1,2})$/);
	if (!match) return null;
	const hrs = Number(match[1]);
	const mins = Number(match[2]);
	if (mins > 59) return null;
	return hrs * MINUTES_PER_HOUR + mins;
};

const OverlappingBanner = ({
	overlappingAction,
	overlappingList = [],
	onOverlappingActionChange,
	cannotCancelOverlaps = false,
	isDelayOptionAvailable = false,
	delayMinutes,
	onDelayMinutesChange,
	delayError,
	minDelayMinutes,
}) => {
	const [draftDelay, setDraftDelay] = useState(null);

	const displayValue = draftDelay != null ? draftDelay : minutesToDuration(delayMinutes);

	const handleDelayChange = useCallback(
		(e) => {
			const raw = e.target.value;
			if (!DURATION_PATTERN.test(raw)) return;
			setDraftDelay(raw);
			const parsed = parseDuration(raw);
			if (parsed != null) {
				onDelayMinutesChange(parsed);
			}
		},
		[onDelayMinutesChange]
	);

	const handleDelayBlur = useCallback(() => {
		setDraftDelay(null);
	}, []);
	const bannerText = cannotCancelOverlaps
		? CANNOT_CANCEL_OVERLAPPING_BANNER_TEXT
		: OVERLAPPING_BANNER_TEXT;

	const isDelaySelected = overlappingAction === OVERLAPPING_ACTION_OPTIONS.DELAY;

	return (
		<>
			<div className={styles.overlappingBanner}>
				<div className={styles.headerBlock}>
					<Icon extraClasses={styles.danger} InlineIcon={IconAttention} size={4} />
					<div className={styles.descriptionBlock}>
						<div className={styles.bold}>{bannerText.title}</div>
						<div>{bannerText.description}</div>
					</div>
				</div>

				<List extraClasses={styles.overlappingList}>
					{overlappingList.map((item, id) => (
						<li key={id}>{item}</li>
					))}
				</List>
			</div>
			{!cannotCancelOverlaps && (
				<>
					<div className={styles.overlappingActionRow}>
						<div className={styles.overlappingActionSelect}>
							<Select
								id="overlapping-activity-action"
								label={OVERLAPPING_ACTION_LABEL}
								value={overlappingAction}
								onChange={(e) => onOverlappingActionChange(e.target.value)}
								placeholder="Select"
								valid={delayError ? false : undefined}
							>
								<SelectOption value={OVERLAPPING_ACTION_OPTIONS.DELETE}>
									{OVERLAPPING_ACTION_OPTION_LABELS[OVERLAPPING_ACTION_OPTIONS.DELETE]}
								</SelectOption>
								{isDelayOptionAvailable && (
									<SelectOption value={OVERLAPPING_ACTION_OPTIONS.DELAY}>
										{OVERLAPPING_ACTION_OPTION_LABELS[OVERLAPPING_ACTION_OPTIONS.DELAY]}
									</SelectOption>
								)}
							</Select>
						</div>
						{isDelaySelected && (
							<div className={styles.delayInput}>
								<Input
									id="overlapping-delay-minutes"
									label={DELAY_LABEL}
									value={displayValue}
									onChange={handleDelayChange}
									onBlur={handleDelayBlur}
									placeholder="HH:MM"
								/>
							</div>
						)}
					</div>
					{isDelaySelected && (
						<>
							{delayError && <div className={styles.delayErrorText}>{delayError}</div>}
							<div className={styles.recommendedDelay}>
								<Icon InlineIcon={IconInfo} size={4} />
								<span>
									{`${MINIMUM_RECOMMENDED_DELAY_TEXT} +${minutesToDuration(minDelayMinutes)}`}
								</span>
							</div>
						</>
					)}
				</>
			)}
		</>
	);
};

export default OverlappingBanner;
